// Roles and the permission matrix.
// Route: /settings/roles

import { useEffect, useMemo, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  Search,
  Table,
  Toggle,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  fmtDate,
  titleCase,
  TriCheckbox,
} from "./shared";

export interface PermissionRef {
  id: string;
  resource: string;
  action: string;
  description?: string;
}

export interface RoleInfo {
  id: string;
  name: string;
  description: string;
  is_system: boolean;
  permissions: PermissionRef[];
}

export interface RoleRow {
  id: string;
  name: string;
  description: string;
  is_system: boolean;
  is_default: boolean;
  /** "resource:action" keys. */
  permissions: string[];
  user_count: number;
  created_at: string;
  updated_at: string;
}

export interface RoleListResponse {
  roles: RoleRow[];
  total: number;
  page: number;
  limit: number;
}

export interface PermissionCatalogEntry {
  id: string;
  resource: string;
  action: string;
  description: string;
  key: string;
}

interface PermissionCatalogResponse {
  permissions: PermissionCatalogEntry[];
}

// ============================================================ RolesView

/**
 * Resources this build ships no screen for. They are hidden from the matrix —
 * but a role that already holds one keeps it, because the selection set starts
 * from the role's full permission list and only visible cells are ever toggled.
 */
const HIDDEN_RESOURCES = new Set([
  "flows.whatsapp",
  "api_keys",
  "webhooks",
  "custom_actions",
  "settings.sso",
  "campaigns",
  "chatbot.ai",
  "chatbot.keywords",
  "canned_responses",
  // The live-agent inbox, agent transfers and the flow builder came out of scope with
  // their screens. Leaving these visible would let an admin grant a permission that
  // reaches nothing, which reads as a capability the product does not have.
  "chat",
  "chat.assign",
  "transfers",
  "flows.chatbot",
]);

const RESOURCE_LABELS: Record<string, string> = {
  users: "Users",
  contacts: "Contacts",
  messages: "Messages",
  teams: "Teams",
  chatbot: "Chatbot",
  templates: "Templates",
  analytics: "Analytics",
  "analytics.agents": "Agent analytics",
  billing: "Billing / usage",
  settings: "Settings",
  "settings.general": "Settings — general",
  "settings.chatbot": "Settings — chatbot",
  webhooks: "Webhooks",
  roles: "Roles",
  tags: "Tags",
  chat: "Chat",
  "chat.assign": "Chat assignment",
  "flows.chatbot": "Chatbot flows",
  accounts: "WhatsApp accounts",
  transfers: "Transfers",
  organizations: "Organisations",
  documents: "Documents",
};

const resourceLabel = (resource: string) => RESOURCE_LABELS[resource] ?? titleCase(resource);

/** Columns run in lifecycle order, so a row reads left to right as it escalates. */
const ACTION_ORDER = [
  "read",
  "write",
  "delete",
  "assign",
  "pickup",
  "import",
  "export",
  "sync",
  "execute",
];

const ACTION_LABELS: Record<string, string> = {
  read: "View",
  write: "Create / edit",
  delete: "Delete",
  manage: "Manage",
  export: "Export",
  import: "Import",
  assign: "Assign",
  pickup: "Pick up",
  sync: "Sync",
  execute: "Execute",
};

const actionLabel = (action: string) => ACTION_LABELS[action] ?? titleCase(action);

interface MatrixRow {
  resource: string;
  label: string;
  /** action -> "resource:action", only for permissions that actually exist. */
  cells: Record<string, string>;
  keys: string[];
}

function buildMatrix(catalogue: PermissionCatalogEntry[]): { rows: MatrixRow[]; actions: string[] } {
  const byResource = new Map<string, MatrixRow>();
  const actions = new Set<string>();

  for (const p of catalogue) {
    if (HIDDEN_RESOURCES.has(p.resource)) continue;
    actions.add(p.action);
    let row = byResource.get(p.resource);
    if (!row) {
      row = { resource: p.resource, label: resourceLabel(p.resource), cells: {}, keys: [] };
      byResource.set(p.resource, row);
    }
    const key = p.key || `${p.resource}:${p.action}`;
    row.cells[p.action] = key;
    row.keys.push(key);
  }

  const ordered = [...actions].sort((a, b) => {
    const ai = ACTION_ORDER.indexOf(a);
    const bi = ACTION_ORDER.indexOf(b);
    if (ai !== bi) return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    return a.localeCompare(b);
  });

  return {
    rows: [...byResource.values()].sort((a, b) => a.label.localeCompare(b.label)),
    actions: ordered,
  };
}

/**
 * 76 permission strings as a resource x action grid rather than a wall of
 * checkboxes: one row per resource, one column per action, and a toggle on
 * every row header, every column header and the corner. A blank cell means the
 * combination does not exist in the catalogue.
 */
function PermissionMatrix({
  catalogue,
  selected,
  disabled,
  onChange,
}: {
  catalogue: PermissionCatalogEntry[];
  selected: Set<string>;
  disabled?: boolean;
  onChange: (next: Set<string>) => void;
}) {
  const { rows, actions } = useMemo(() => buildMatrix(catalogue), [catalogue]);

  const setMany = (keys: string[], on: boolean) => {
    if (disabled) return;
    const next = new Set(selected);
    for (const key of keys) {
      if (on) next.add(key);
      else next.delete(key);
    }
    onChange(next);
  };

  if (rows.length === 0) {
    return (
      <Empty
        title="No permissions available"
        body="The permission catalogue is empty. Check that permissions are seeded in the database."
        icon="⊘"
      />
    );
  }

  const allKeys = rows.flatMap((r) => r.keys);
  const allOn = allKeys.every((k) => selected.has(k));
  const allSome = allKeys.some((k) => selected.has(k));

  const columnKeys = (action: string) =>
    rows.map((r) => r.cells[action]).filter((k): k is string => Boolean(k));

  return (
    <div className="table-wrap">
      <table className="data permgrid">
        <thead>
          <tr>
            <th>
              <div className="row">
                <TriCheckbox
                  checked={allOn}
                  indeterminate={allSome}
                  disabled={disabled}
                  ariaLabel="Select every permission"
                  onChange={(on) => setMany(allKeys, on)}
                />
                <span>Resource</span>
              </div>
            </th>
            {actions.map((action) => {
              const keys = columnKeys(action);
              const on = keys.every((k) => selected.has(k));
              const some = keys.some((k) => selected.has(k));
              return (
                <th key={action}>
                  <div className="col-head">
                    <span>{actionLabel(action)}</span>
                    <TriCheckbox
                      checked={on}
                      indeterminate={some}
                      disabled={disabled}
                      ariaLabel={`Select ${actionLabel(action)} for every resource`}
                      onChange={(next) => setMany(keys, next)}
                    />
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const on = row.keys.every((k) => selected.has(k));
            const some = row.keys.some((k) => selected.has(k));
            const count = row.keys.filter((k) => selected.has(k)).length;
            return (
              <tr key={row.resource}>
                <td>
                  <div className="row">
                    <TriCheckbox
                      checked={on}
                      indeterminate={some}
                      disabled={disabled}
                      ariaLabel={`Select every ${row.label} permission`}
                      onChange={(next) => setMany(row.keys, next)}
                    />
                    <span className="spacer">{row.label}</span>
                    <span className="muted num">
                      {count}/{row.keys.length}
                    </span>
                  </div>
                </td>
                {actions.map((action) => {
                  const key = row.cells[action];
                  return (
                    <td key={action}>
                      {key ? (
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          disabled={disabled}
                          aria-label={`${row.label}: ${actionLabel(action)}`}
                          onChange={(e) => setMany([key], e.target.checked)}
                        />
                      ) : (
                        <span className="na" aria-hidden>
                          ·
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RolesView() {
  const { me, can } = useSession();
  const toast = useToast();
  const canWrite = can("roles", "write");
  const canDeleteRole = can("roles", "delete");
  const isSuperAdmin = Boolean(me?.is_super_admin);

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<RoleListResponse>(
    () => http.get<RoleListResponse>(`/roles${qs({ search: query, page, limit: PAGE_SIZE })}`),
    [query, page]
  );
  const roles = data?.roles ?? [];
  const total = data?.total ?? roles.length;

  const catalogue = useAsync<PermissionCatalogResponse>(
    () => http.get<PermissionCatalogResponse>("/permissions"),
    []
  );
  const permissions = catalogue.data?.permissions ?? [];

  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [toDelete, setToDelete] = useState<RoleRow | null>(null);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setIsDefault(false);
    setSelected(new Set());
    setDialogOpen(true);
  };

  const openEdit = (role: RoleRow) => {
    setEditing(role);
    setName(role.name);
    setDescription(role.description ?? "");
    setIsDefault(role.is_default);
    // Seeded with the role's FULL list, hidden resources included, so saving
    // never silently strips a permission the matrix does not render.
    setSelected(new Set(role.permissions));
    setDialogOpen(true);
  };

  // A system role is read-only unless you are a super admin.
  const readOnly = Boolean(editing?.is_system) && !isSuperAdmin;
  const canEditPermissions = !readOnly && canWrite;

  const hiddenHeld = useMemo(
    () => [...selected].filter((key) => HIDDEN_RESOURCES.has(key.split(":")[0])),
    [selected]
  );

  const save = async () => {
    if (!name.trim()) {
      toast.push("Enter a role name", "critical");
      return;
    }
    setSubmitting(true);
    const body = {
      name: name.trim(),
      description: description.trim(),
      is_default: isDefault,
      permissions: [...selected],
    };
    try {
      if (editing) {
        await http.put(`/roles/${editing.id}`, body);
        toast.push("Role updated", "good");
      } else {
        await http.post("/roles", body);
        toast.push("Role created", "good");
      }
      setDialogOpen(false);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not save the role"), "critical");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/roles/${toDelete.id}`);
      toast.push("Role deleted", "good");
      setToDelete(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not delete the role"), "critical");
      throw e;
    }
  };

  const columns: Column<RoleRow>[] = [
    {
      key: "name",
      header: "Role",
      sortable: true,
      render: (r) => (
        <div className="row">
          <span className="strong">{titleCase(r.name)}</span>
          {r.is_system && <Badge title="Seeded with the organisation">System</Badge>}
          {r.is_default && <Badge tone="brand">Default</Badge>}
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      sortable: true,
      render: (r) => <span className="truncate">{r.description || "—"}</span>,
    },
    {
      key: "permissions",
      header: "Permissions",
      align: "right",
      render: (r) => <Badge>{r.permissions.length}</Badge>,
    },
    { key: "user_count", header: "Users", align: "right", sortable: true },
    { key: "created_at", header: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button
            className="btn icon sm ghost"
            title={
              r.is_system
                ? isSuperAdmin
                  ? "Edit this system role's permissions"
                  : "View this system role's permissions"
                : "Edit this role"
            }
            aria-label={`Open ${r.name}`}
            onClick={() => openEdit(r)}
          >
            {r.is_system && !isSuperAdmin ? "◉" : "✎"}
          </button>
          {!r.is_system && (
            <button
              className="btn icon sm ghost"
              title={
                r.user_count > 0
                  ? "Cannot delete: users still hold this role"
                  : "Delete this role"
              }
              aria-label={`Delete ${r.name}`}
              disabled={!canDeleteRole || r.user_count > 0}
              onClick={() => setToDelete(r)}
            >
              ✕
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Roles</h1>
          <div className="sub">What each role may do, resource by resource.</div>
        </div>
        <div className="page-actions">
          <button className="btn primary" disabled={!canWrite} onClick={openCreate}>
            + Add role
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the roles" />
      <ErrorBanner error={catalogue.error} what="Could not load the permission catalogue" />

      <div className="card flush">
        <div className="card-head">
          <h3>Your roles</h3>
          <Search value={search} onChange={setSearch} placeholder="Search roles…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={roles}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching roles" : "No roles yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "The three system roles are seeded with the organisation. Add your own when they do not fit."
                }
                icon="⛊"
                action={
                  !query && canWrite ? (
                    <button className="btn primary sm" onClick={openCreate}>
                      + Add role
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="roles"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        wide
        title={editing ? (readOnly ? "View role" : "Edit role") : "Create role"}
      >
        {editing?.is_system && (
          <Banner tone={isSuperAdmin ? "warning" : "info"}>
            {isSuperAdmin
              ? "This is a system role. You can change its permissions because you are a super admin — every user holding it is affected immediately."
              : "This is a system role. It is shown read-only; only a super admin can change it."}
          </Banner>
        )}

        <Field label="Name" required>
          <input
            value={name}
            placeholder="Support lead"
            disabled={Boolean(editing?.is_system)}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <textarea
            value={description}
            rows={2}
            placeholder="What this role is for."
            disabled={readOnly}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        {!editing?.is_system && (
          <div className="setting">
            <div className="text">
              <div className="strong">Default role</div>
              <p>New members of this organisation get this role. Only one role can be the default.</p>
            </div>
            <Toggle
              checked={isDefault}
              disabled={readOnly}
              ariaLabel="Default role"
              onChange={setIsDefault}
            />
          </div>
        )}

        <div className="section">
          <div className="row" style={{ marginBottom: "var(--sp-2)" }}>
            <h4 className="spacer" style={{ marginBottom: 0 }}>
              Permissions
            </h4>
            <span className="muted num">{selected.size} selected</span>
          </div>
          <p className="muted" style={{ marginBottom: "var(--sp-3)" }}>
            Tick a cell for one permission, a row header for every action on that resource, or a
            column header for that action across every resource.
          </p>
          {catalogue.loading ? (
            <div className="row">
              <span className="spinner" />
              <span className="muted">Loading permissions…</span>
            </div>
          ) : (
            <PermissionMatrix
              catalogue={permissions}
              selected={selected}
              disabled={!canEditPermissions}
              onChange={setSelected}
            />
          )}
          {hiddenHeld.length > 0 && (
            <p className="muted" style={{ marginTop: "var(--sp-3)" }}>
              {hiddenHeld.length} permission{hiddenHeld.length === 1 ? "" : "s"} on features this
              build does not expose ({hiddenHeld.join(", ")}) are held by this role and kept
              unchanged when you save.
            </p>
          )}
        </div>

        <DialogFooter>
          <button className="btn" onClick={() => setDialogOpen(false)} disabled={submitting}>
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button
              className="btn primary"
              onClick={() => void save()}
              disabled={submitting || !canWrite}
            >
              {submitting && <span className="spinner" />}
              {editing ? "Update role" : "Create role"}
            </button>
          )}
        </DialogFooter>
      </Dialog>

      <Confirm
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
        danger
        title="Delete role"
        body={
          <>
            <strong className="strong">{toDelete?.name}</strong> is deleted. This cannot be undone.
          </>
        }
      />
    </div>
  );
}
